"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Wire dates. One helper, used at every boundary.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **This file is `hard-won.md` P1-T8-2 paid forward.** Three sightings of one class in
 * three places — a cursor truncated to milliseconds, a cursor built at local midnight, a
 * serialiser off by one day — were a missing abstraction rather than three mistakes. A fourth
 * was found live, in `tasks.service.ts`, *under a comment warning about the trap it had fallen
 * into*. So: one place, and everything goes through it.
 *
 * ⚠️ **A CALENDAR DATE and an INSTANT are different things, and one converter cannot serve
 * both.** That is why there is a pair rather than a `toWire()`:
 *
 * - a **calendar date** ("the task is due on the 24th") has no time and no zone. It must be
 *   formatted from LOCAL parts. `toISOString().slice(0, 10)` returns the PREVIOUS day for
 *   every date east of Greenwich — Budapest is UTC+1, so this is every date, all year, and
 *   `docs/mockup/main.jsx` does exactly this in `setRepPeriod`;
 * - an **instant** ("the comment was posted at 12:04") is a point in time and UTC is correct
 *   for it.
 *
 * A generic helper would have to guess which, and it would guess wrong silently.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.toWireDate = toWireDate;
exports.fromWireDate = fromWireDate;
exports.toWireTimestamp = toWireTimestamp;
exports.addWireDays = addWireDays;
exports.wireDayOfWeek = wireDayOfWeek;
exports.wireMonday = wireMonday;
exports.isoWeek = isoWeek;
exports.wireToday = wireToday;
const pad = (n) => String(n).padStart(2, '0');
const pad4 = (n) => String(n).padStart(4, '0');
const WIRE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * A calendar date, from the date's **local** parts.
 *
 * ⚠️ Never `toISOString().slice(0, 10)`. `new Date(2026, 7, 24)` is midnight local, which is
 * `2026-08-23T22:00:00Z` in Budapest — so the UTC route returns the 23rd. The driver hands
 * back a `Date` at local midnight for a `date` column too, so this is the trap on both the
 * read and the write side (`hard-won.md` P1-T7-6).
 */
function toWireDate(date) {
    /* `pad` for the year too, so nothing here interpolates a raw number — `getFullYear()` in a
     * template literal is a `restrict-template-expressions` error, and the lint rule is right:
     * a number's default stringification is a locale-independent accident everywhere except
     * where it happens to be what you wanted. */
    return `${pad4(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
/**
 * A calendar date back to a `Date` at **local** midnight — the exact inverse of `toWireDate`.
 *
 * ⚠️ Not `new Date('2026-08-24')`. A bare date string is parsed as UTC by specification, so
 * that expression is 02:00 local in Budapest, and `getDate()` on it is right until somebody
 * subtracts a day near a DST boundary. Passing the parts explicitly asks for local midnight.
 *
 * Returns `null` for anything that is not `yyyy-mm-dd`, and for a well-formed string that
 * names no real day — `2026-02-30` parses and then does not round-trip, which is how a typo
 * in a filter becomes a silent off-by-two.
 */
function fromWireDate(wire) {
    const match = WIRE_DATE.exec(wire);
    if (!match)
        return null;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(year, month - 1, day);
    /* `new Date(2026, 1, 30)` is 2 March, not an error. The round-trip is the only cheap check. */
    return toWireDate(date) === wire ? date : null;
}
/**
 * An instant, in UTC. The other half of the pair.
 *
 * ⚠️ **Do not reach for this for a due date or a worklog day.** It is correct for "when did
 * this happen" and loses a day for "which day is this".
 */
function toWireTimestamp(date) {
    return date.toISOString();
}
/**
 * `n` days from a wire date, staying in wire form.
 *
 * Goes through local parts, so it is DST-safe: `new Date(y, m, d + n)` normalises across month
 * and year boundaries without ever touching a timezone offset. Adding `86_400_000` to a
 * timestamp does not — it is an hour short twice a year.
 */
function addWireDays(wire, days) {
    const match = WIRE_DATE.exec(wire);
    if (!match)
        return null;
    return toWireDate(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
}
/**
 * Day of the week, **Monday = 0**.
 *
 * The product's week starts on Monday everywhere — the pickers, the timesheet, the Gantt — and
 * JavaScript's `getDay()` starts on Sunday. Converting at each call site is how one of them
 * ends up a day out; `docs/mockup/main.jsx` writes `(getDay() + 6) % 7` in four places.
 */
function wireDayOfWeek(wire) {
    const date = fromWireDate(wire);
    return date === null ? null : (date.getDay() + 6) % 7;
}
/** The Monday of the wire date's week. */
function wireMonday(wire) {
    const dow = wireDayOfWeek(wire);
    return dow === null ? null : addWireDays(wire, -dow);
}
/**
 * The ISO-8601 week number of a wire date. Thursday decides the year, which is the whole rule.
 *
 * ⚠️ 16 Oct 2026 · moved here from the API's `dashboards.service.ts`: Időrögzítés names the week
 * it shows (*34. hét*), and a second copy of this is two answers in the last week of December.
 */
function isoWeek(wire) {
    const date = new Date(`${wire}T00:00:00Z`);
    const day = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - day + 3);
    const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
    const firstDay = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDay + 3);
    return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}
/**
 * Today, in the member's zone, as a wire date.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C51.2 · MOVED HERE FROM `agent.service.ts`, BECAUSE A SECOND CALLER   │
 * │ ARRIVED AND THIS IS THE ONE FUNCTION THAT MUST NOT HAVE TWO VERSIONS.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: *"One helper for wire dates, used at every boundary."* It was private to the agent
 * while the agent was the only thing that needed to know what day it is for a member; the MCP
 * server needs the identical answer, and `mcp.md` §7 is emphatic that a foreign model's idea of
 * today comes from a training cutoff and a host's header and is routinely wrong. Two copies of this
 * is two ideas of today, which is the failure it was written to prevent in the first place.
 *
 * ⚠️⚠️ **ONE VALUE PER TURN, HANDED BOTH TO THE MODEL AND TO THE TOOLS.** The date note tells the
 * model *"today is 2026-09-01"*; `worklogs_overview` defaults its week to `ToolContext.today`. Two
 * `new Date()` calls could disagree by a day for part of every evening in Budapest — and the answer
 * would be the wrong week's figures under a confident sentence about this one.
 *
 * ⚠️ **A WIRE DATE, and `hard-won.md`'s driver rows are why.** A calendar date and an instant are
 * different things; the agent talks about days — *yesterday*, *last week*, *the 1st* — and every one
 * of those crosses into `worked_on`, which is a `date`. `toISOString().slice(0, 10)` is UTC's idea
 * of today, which is the wrong day for part of every evening in Budapest; `en-CA` formats
 * `YYYY-MM-DD` in the named zone.
 *
 * ⚠️⚠️ **THE MEMBER'S OWN ZONE FROM C27.4, AND THE HARDCODED `Europe/Budapest` IT REPLACES WAS THE
 * FIRST ROW ON `debt.md`'s C27 LIST.** It had two consumers by then — the date the model is told, and
 * the week `worklogs_overview` defaults to — so a member in another zone was answered about the wrong
 * day with a confident sentence above it. `user_preferences.timezone` is where it lives, and
 * `conversation.service.ts` reads it in the same query as the locale.
 *
 * ⚠️ **The fallback is a `try`, and it is not defensive clutter.** `Intl.DateTimeFormat` **throws** a
 * `RangeError` on a zone it does not know, and Postgres's tz database and Node's ICU can disagree
 * about a rare one — which would kill every turn for that member with an `upstream_unavailable` that
 * names nothing. A slightly wrong day beats a dead agent, and 0010's `is_valid_timezone` check is
 * what makes this branch nearly unreachable rather than load-bearing.
 */
function wireToday(now, timezone) {
    const format = (zone) => new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
    try {
        return format(timezone);
    }
    catch {
        return format('Europe/Budapest');
    }
}
//# sourceMappingURL=dates.js.map